import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

// Fetch Microsoft 365 profile photo using the provider token
const fetchMicrosoftPhoto = async (providerToken: string): Promise<void> => {
  try {
    console.log('📸 Fetching Microsoft 365 profile photo...');
    const photoRes = await fetch(
      'https://graph.microsoft.com/v1.0/me/photos/96x96/$value',
      { headers: { Authorization: `Bearer ${providerToken}` } }
    );
    if (photoRes.ok) {
      const blob = await photoRes.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        localStorage.setItem('ms_profile_photo', base64);
        console.log('✅ Microsoft 365 profile photo saved');
      };
      reader.readAsDataURL(blob);
    } else {
      console.log('📸 No profile photo available (status:', photoRes.status, ')');
    }
  } catch (err) {
    console.warn('📸 Could not fetch Microsoft profile photo:', err);
  }
};

export const AuthCallback: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      console.log("🔄 AuthCallback mounted. Attempting to handle OAuth callback.");
      console.log("🔍 AuthCallback: Full URL:", window.location.href);
      console.log("🔍 AuthCallback: Search params:", window.location.search);
      console.log("🔍 AuthCallback: Hash:", window.location.hash);
      
      // Check if we have OAuth parameters in URL (both search and hash)
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      
      const code = urlParams.get('code') || hashParams.get('code');
      const accessToken = hashParams.get('access_token');
      const error = urlParams.get('error') || hashParams.get('error');
      
      console.log('📊 OAuth callback URL params:', { 
        code: !!code, 
        codeValue: code?.substring(0, 10) + '...',
        accessToken: !!accessToken,
        accessTokenValue: accessToken?.substring(0, 20) + '...',
        error,
        searchParamsCount: Array.from(urlParams.entries()).length,
        hashParamsCount: Array.from(hashParams.entries()).length
      });
      
      if (error) {
        console.error('OAuth error in callback:', error);
        console.error('Full URL params:', Object.fromEntries(urlParams.entries()));
        console.error('Current URL:', window.location.href);
        console.error('Current domain:', window.location.origin);
        
        // Log additional error details if available
        const errorDescription = urlParams.get('error_description') || hashParams.get('error_description');
        const errorUri = urlParams.get('error_uri') || hashParams.get('error_uri');
        if (errorDescription) {
          console.error('OAuth error description:', errorDescription);
        }
        if (errorUri) {
          console.error('OAuth error URI:', errorUri);
        }
        
        // Check if it's a redirect URI mismatch
        if (errorDescription?.includes('redirect_uri') || error.includes('redirect')) {
          console.error('⚠️ REDIRECT URI MISMATCH - Check Azure AD and Supabase configurations');
          console.error('Expected redirect URI:', `${window.location.origin}/auth/callback`);
        }
        
        navigate('/login');
        return;
      }
      
      // Handle implicit flow (access_token in hash) or authorization code flow (code in params)
      if (!code && !accessToken) {
        console.warn('No OAuth code or access token found in callback URL');
        console.log('📊 Full URL breakdown:', {
          href: window.location.href,
          search: window.location.search,
          hash: window.location.hash,
          searchParams: Object.fromEntries(urlParams.entries()),
          hashParams: Object.fromEntries(hashParams.entries())
        });
        navigate('/login');
        return;
      }

      try {
        if (accessToken) {
          // Handle implicit flow - access token is already in the URL hash
          console.log('🔄 Handling implicit flow OAuth callback with access_token...');
          console.log('🔑 Access token received (first 20 chars):', accessToken.substring(0, 20) + '...');
          
          // For implicit flow, Supabase should automatically detect and set the session
          // Check session immediately without delay
          console.log('🔍 Getting session after implicit flow callback...');
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          console.log('📊 Session retrieval result:', {
            hasSession: !!session,
            hasError: !!sessionError,
            userId: session?.user?.id,
            userEmail: session?.user?.email,
            sessionExpiry: session?.expires_at
          });
          
          if (sessionError) {
            console.error('❌ Error getting session after implicit flow:', sessionError);
            navigate('/login');
            return;
          }
          
          if (session) {
            console.log('✅ Implicit flow OAuth session successfully created!');
            console.log('👤 User ID:', session.user.id);
            console.log('📧 User email:', session.user.email);
            console.log('⏰ Session expires at:', new Date(session.expires_at! * 1000));

            // Fetch Microsoft 365 profile photo
            if (session.provider_token) {
              await fetchMicrosoftPhoto(session.provider_token);
            }

            // The trigger function handles profile creation/update
            console.log('🔄 Redirecting after successful implicit flow...');
            const redirectPath = sessionStorage.getItem('redirectAfterLogin') || '/';
            sessionStorage.removeItem('redirectAfterLogin');
            navigate(redirectPath, { replace: true });
            return;
          } else {
            console.warn('⚠️ No session found after implicit flow callback');
            navigate('/login');
            return;
          }
        }
        
        if (code) {
          // Handle authorization code flow
          console.log('🔄 Attempting to exchange OAuth code for session...');
          console.log('🔍 Full callback URL:', window.location.href);
          
          // Check if session already exists
          console.log('🔍 Checking if session already exists...');
          let { data: { session: existingSession } } = await supabase.auth.getSession();
          console.log('📊 Existing session check:', existingSession ? '✅ Found' : '❌ None');
          
          // Try to exchange the code for a session immediately
          if (!existingSession && code) {
            console.log('🔄 Exchanging code for session...');
            const { data: sessionData, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
            
            if (exchangeError) {
              console.error('❌ Error exchanging code for session:', exchangeError);
              navigate('/login');
              return;
            }
            
            if (sessionData.session) {
              console.log('✅ Code exchange successful!');
              existingSession = sessionData.session;
            }
          }
          
          console.log('🔍 Getting final session after OAuth callback...');
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          console.log('📊 Session retrieval result:', {
            hasSession: !!session,
            hasError: !!sessionError,
            userId: session?.user?.id,
            userEmail: session?.user?.email,
            sessionExpiry: session?.expires_at
          });
          
          if (sessionError) {
            console.error('❌ Error getting session after callback:', sessionError);
            console.error('❌ Session error details:', JSON.stringify(sessionError, null, 2));
            navigate('/login');
            return;
          }
          
          if (session) {
            console.log('✅ OAuth session successfully created!');
            console.log('👤 User ID:', session.user.id);
            console.log('📧 User email:', session.user.email);
            console.log('🔑 Access token (first 20 chars):', session.access_token?.substring(0, 20) + '...');
            console.log('⏰ Session expires at:', new Date(session.expires_at! * 1000));

            // Fetch Microsoft 365 profile photo
            if (session.provider_token) {
              await fetchMicrosoftPhoto(session.provider_token);
            }

            // The trigger function handles profile creation/update
            console.log('🔄 Redirecting after successful OAuth callback...');
            const redirectPath = sessionStorage.getItem('redirectAfterLogin') || '/';
            sessionStorage.removeItem('redirectAfterLogin');
            navigate(redirectPath, { replace: true });
          } else {
            console.warn('⚠️ No session found after OAuth callback. This might indicate:');
            console.warn('1. OAuth flow was interrupted');
            console.warn('2. Session creation failed');
            console.warn('3. Token exchange failed');
            console.warn('4. Supabase configuration issue');
            
            navigate('/login');
          }
        }
      } catch (error) {
        console.error('💥 Critical error in OAuth callback handler:', error);
        console.error('💥 Error stack:', error instanceof Error ? error.stack : 'No stack trace');
        navigate('/login');
      }
    };
    handleCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface">
      <div className="text-navy">Completing sign in...</div>
    </div>
  );
}; 